import { defineStore } from 'pinia'
import {
  getDefaultLocation,
  getDefaultMethod,
  useLastCart,
  useWindow
} from '../../composables'
import { ImprintMethodModel } from '../../model'
import { OrderType, type ArtworkFile, type LocationGroup, type LocationMethod } from '../../types'
import { useApp } from './app'
import { useProductSpec } from './productSpec'

interface SpecState {
  editIndex: number
  selectedList: ImprintMethodModel[]
}

export type ImprintMethodCreateOptions = {
  location: string
  method?: string
}

export const useImprintMethod = defineStore('imprintMethod', {
  state: (): SpecState => {
    return {
      editIndex: -1,
      selectedList: []
    }
  },
  getters: {
    getEditIndex (): number {
      return this.editIndex
    },
    getSelectedList (): ImprintMethodModel[] {
      return this.selectedList
    },

    isShowAddAnother () {
      const appState = useApp()
      const { locationGroupList } = useWindow()
      // 如果选择的是blank，那就不显示add another location按钮
      if (appState.getBlankValue) return false
      // 如果是Blank就不显示add another location按钮
      if (appState.getBlankLimitation === 2) return false
      // 如果是spec sample，就不显示按钮
      if (appState.getOrderType === OrderType.SpecSample) return false
      // 如果没有记录，那就不显示add location按钮
      if (this.selectedList.length === 0) return false

      if (this.selectedList.length >= appState.maxLocationNum) return false

      return this.getSelectedList.length < locationGroupList.filter((f) => f.Enabled).length

    }
  },
  actions: {
    setEditIndex (val: number): void {
      this.editIndex = val
    },
    /**
     * 创建一个新的 imprint method
     */
    create (options?: ImprintMethodCreateOptions): void {
      const { locationGroupList, pmsColorList, defaultArtworkFile } = useWindow()
      const appState = useApp()
      const model = new ImprintMethodModel()
      const locationGroups = locationGroupList.filter(
        (f) =>
          this.selectedList.some((s) => s.decorationMethod.Location === f.Name) === false
      )
      let currentLocation: LocationGroup | undefined
      let currentMethod: LocationMethod | undefined

      if (options) {
        currentLocation = locationGroups.find(
          (f) => f.Enabled && f.Name === options.location
        )
        if (currentLocation) {
          if (options.method) {
            currentMethod = currentLocation.Methods.find(
              (f) => f.Enabled && f.Method === options.method
            )
          } else {
            currentMethod = currentLocation.Methods.find((f) => f.Enabled)
          }
        }
      } else if (
        appState.samePreviousChecked &&
        appState.samePreviousDisaled === false
      ) {
        const cartInfo = useLastCart()
        if (cartInfo) {
          currentLocation = locationGroups.find((f) =>
            cartInfo.ImprintOptions.some(
              (imprint) => imprint.Location === f.Name
            )
          )
          if (currentLocation) {
            currentMethod = currentLocation.Methods.find((f) =>
              cartInfo.ImprintOptions.some(
                (imprint) => imprint.Method === f.Method
              )
            )
            if (currentMethod) {
              const currentImprintOption = cartInfo.ImprintOptions.find(
                (f) =>
                  f.Location === currentMethod!.Location &&
                  f.Method === currentMethod!.Method
              )

              if (currentImprintOption) {
                // 开始拷贝数据
                const selectedStandardColors =
                  currentMethod.DisplayColors.filter((f) =>
                    currentImprintOption.ImprintColor.some(
                      (color) => color === f.Name
                    )
                  )

                // 标准颜色
                currentMethod.selectedStandardColors.push(
                  ...selectedStandardColors
                )
                currentMethod.selectedColors.push(...selectedStandardColors)

                // PMS color
                const selectedPmsColors = pmsColorList.filter((f) =>
                  cartInfo.ImprintOptions.find((imprint) =>
                    imprint.ImprintColor.some((color) => color === f.Name)
                  )
                )

                currentMethod.selectedPmsColors.push(...selectedPmsColors)
                currentMethod.selectedColors.push(...selectedPmsColors)

                // artwork选项
                currentMethod.artworkType = currentImprintOption.ArtworkOption
                currentMethod.selectedArtworkFiles.push(
                  ...currentImprintOption.ArtworkFiles.map((f) => ({
                    BoxNetFileSysNo: f.BoxNetFileSysNo,
                    DownloadUrl: f.Url,
                    PreviewUrl: f.Url,
                    FileName: f.FileName,
                    FileExt: f.Extension,
                    ThumbnailUrl: f.ThumbnailUrl
                  } as ArtworkFile))
                )

                currentMethod.customerNotes = currentImprintOption.ArtworkText

                if (currentImprintOption.VirtualDesignArts) {
                  currentMethod.virtualDesignArts =
                    currentImprintOption.VirtualDesignArts
                  currentMethod.virtualDesignFonts =
                    currentImprintOption.VirtualDesignFonts
                  currentMethod.virtualDesignImage =
                    currentImprintOption.VirtualDesignImage
                  currentMethod.virtualDesignLogo =
                    currentImprintOption.VirtualDesignLogo
                  currentMethod.virtualDesignFileId =
                    currentImprintOption.VirtualDesignFileId
                }
              }
            }
          }
        }
      }

      if (!currentMethod) {
        // 先获取URL里的参数

        const { pageInfo } = (window as any)
        const methodParam = pageInfo.defaultMethod
        // 如果URL里有method参数，那就默认选中地址栏对应的参数

        if (methodParam) {
          currentLocation = locationGroups.find(f => f.Enabled && f.Selected === false
            && f.Methods.some(m => m.Enabled && m.MappedMethod === methodParam))
        }

        if (!currentLocation) {
          currentLocation = getDefaultLocation(locationGroups)
        }
        if (methodParam) {
          currentMethod = currentLocation?.Methods.find(f => f.Enabled && f.MappedMethod === methodParam)
        }
        if (!currentMethod) {
          currentMethod = getDefaultMethod(currentLocation)
        }

      }

      if (currentMethod) {
        if (defaultArtworkFile != null) {
          // 先判断有没有记录
          if (currentMethod.selectedArtworkFiles.length === 0 ) {
            currentMethod.selectedArtworkFiles.push({
              BoxNetFileSysNo: defaultArtworkFile.SysNo,
              DownloadUrl: defaultArtworkFile.Url,
              PreviewUrl: defaultArtworkFile.Url,
              FileName: defaultArtworkFile.FileName,
              FileExt: defaultArtworkFile.Extension,
              ThumbnailUrl: defaultArtworkFile.ThumbnailUrl
            } as ArtworkFile)
          }
        }
        model.decorationMethod = currentMethod;


        this.add(model)
        const newEditIndex = this.selectedList.length - 1
        this.setEditIndex(newEditIndex)
      }
    },
    add (item: ImprintMethodModel): void {
      this.selectedList.push(item)
    },
    /**
     *
     * @param item
     */
    remove (item: ImprintMethodModel): void {
      const index = this.selectedList.findIndex(
        (f) => f.objectId === item.objectId
      )
      if (index !== -1) {
        this.selectedList.splice(index, 1)
      }
    },
    /**
     *
     * @param index
     * @returns
     */
    removeAt (index: number): void {
      if (index >= this.selectedList.length) {
        return
      }
      if (index === -1) {
        return
      }
      this.selectedList.splice(index, 1)
    },
    checkSameColor (): void {
      const isAbTest = (window as any)._customizeSameColorTest === '1'
      if (isAbTest) {
        const productSpecState = useProductSpec()
        this.getSelectedList.forEach((item) => {
          // 判断所选的颜色
          const sameColor = productSpecState.getSelectedList.some(
            (specItem) => {
              if (specItem.productSpec.length === 1) {
                return item.decorationMethod.selectedColors.some(
                  (color) =>
                    color.Name === specItem.productSpec[0].value.mapColor
                )
              }
              return false
            }
          )

          item.decorationMethod.sameColorError = sameColor
        })
      }
    },
    empty (): void {
      this.selectedList.splice(0)
    }
  }
})
